import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl'
import { connect } from 'react-redux'
import classnames from 'classnames'
import { Row, Col } from 'react-flexbox-grid'
import Button from 'components/util/Button'
import Svg from 'components/util/Svg'
import Loader from 'components/util/Loader'
import camera from 'images/camera.png'
import * as feedSelectors from 'services/feed/selectors'
import * as feedActions from 'services/feed/actions'
import * as user from 'services/user/selectors'
import styles from './_createActivityStory.css'

const messages = defineMessages({
  textPlaceholder: {
    id: 'storytext.placeholder',
    defaultMessage: 'Add a status update here...',
  },
})

const mapStateToProps = (state) => ({
  createActivityStoryLoading: feedSelectors.selectCreateActivityStoryLoading(state),
  userId: user.selectUserId(state),
  profileImg: user.selectProfileImg(state),
})

const mapDispatchToActions = {
  ...feedActions,
}

const privacyStatuses = {
  private: 0,
  friends: 1,
  public: 2,
}

const FILE_UPLOAD_LIMIT = 20

export class CreateActivityStory extends Component {
  static propTypes = {
    userId: PropTypes.number,
    createActivityStory: PropTypes.func,
    intl: PropTypes.object,
    createActivityStoryLoading: PropTypes.bool,
    profileImg: PropTypes.string,
  }

  state = {
    storyText: '',
    storyTextError: false,
    privacyStatus: privacyStatuses.public,
    imagePreviewUrl: null,
    file: null,
    fileValidationError: false,
  }

  handleCreateActivityStory = () => {
    const { userId, createActivityStory } = this.props
    const { storyText, privacyStatus, file } = this.state

    if (storyText.length) {
      createActivityStory(userId, storyText, privacyStatus, file).then(() => {
        this.setState({
          storyText: '',
          imagePreviewUrl: null,
        })
      })
    } else {
      this.setState({
        storyTextError: true,
      })
    }
  }

  handleStoryTextChange = (e) => {
    this.setState({
      storyText: e.target.value,
      storyTextError: false,
    })
  }

  handlePrivacyUpdate = (status) => {
    this.setState({
      privacyStatus: status,
    })
  }

  handleImageUploadStart = () => {
    document.getElementById('statusImageInput').click()
  }

  handleImageChange = (e) => {
    e.preventDefault()

    const reader = new FileReader()
    const file = e.target.files[0]

    reader.readAsDataURL(file)

    reader.onloadend = () => {
      const fileSize = file.size / 1024 / 1024

      if (fileSize > FILE_UPLOAD_LIMIT) {
        this.setState({
          fileValidationError: true,
        })
      } else {
        this.setState({
          file: file,
          imagePreviewUrl: reader.result,
          fileValidationError: false,
        })
      }
    }
  }

  handleUploadCancel = () => {
    this.setState({
      imagePreviewUrl: null,
      file: null,
      fileValidationError: false,
    })
  }

  render () {
    const {
      intl,
      createActivityStoryLoading,
      profileImg,
    } = this.props

    const {
      imagePreviewUrl,
      storyTextError,
      fileValidationError,
    } = this.state

    const selectedPrivacy = this.state.privacyStatus

    return (
      <div>
        <div className={styles.storyTextContainer}>
          <Row>
            <Col sm={1} xs={3}>
              <img
                className={styles.avatar}
                src={profileImg}
              />
            </Col>
            <Col sm={11} xs={9}>
              <textarea
                placeholder={intl.formatMessage(messages.textPlaceholder)}
                value={this.state.storyText}
                onChange={this.handleStoryTextChange}
                className={styles.storyText}
              />
              {storyTextError ?
                <div className={styles.error}>
                  <FormattedMessage id='activitystory.text.required' defaultMessage='Please enter some text.' />
                </div>
              : null}

              {fileValidationError ?
                <div className={styles.error}>
                  <FormattedMessage id='activitystory.file.error' defaultMessage='The file cannot exceed 20MB.' />
                </div>
              : null}
            </Col>
          </Row>
        </div>

        {imagePreviewUrl ?
          <Row>
            <Col smOffset={1} sm={11} xs={12}>
              <div className={styles.uploadPreviewContainer}>
                <div className={styles.uploadPreviewHeader}>
                  <span className={styles.uploadHeaderText}>
                    <FormattedMessage id='activitystory.mediapreview' defaultMessage='Media Upload Preview:' />
                  </span>
                  <Svg className={styles.closeButton} name='x' onClick={this.handleUploadCancel} />
                </div>
                <img className={styles.upload} src={imagePreviewUrl} />
              </div>
            </Col>
          </Row> : null}
        <Row>
          <Col smOffset={1} sm={11} xs={12}>
            <div className={styles.optionsRow}>
              <img className={styles.camera} src={camera} onClick={this.handleImageUploadStart} />
              <form className={styles.imageUploadForm} encType='multipart/form-data'>
                <input
                  onChange={this.handleImageChange}
                  id='statusImageInput'
                  type='file'
                  name='image'
                  accept='image/gif, image/jpeg, image/jpg, image/bmp, image/png'
                />
              </form>
              <div className={styles.privacyOptions}>
                <div className={styles.postVisibility}>
                  <FormattedMessage id='activitystory.post.visibility' defaultMessage='Post Visibility:' />
                </div>
                <div
                  onClick={() => this.handlePrivacyUpdate(privacyStatuses.private)}
                  className={classnames({
                    [styles.privacyIcon]: true,
                    [styles.privacyIconPrivate]: true,
                    [styles.privacyIconPrivateActive]: selectedPrivacy === privacyStatuses.private,
                  })}
                />
                <div
                  onClick={() => this.handlePrivacyUpdate(privacyStatuses.friends)}
                  className={classnames({
                    [styles.privacyIcon]: true,
                    [styles.privacyIconFriends]: true,
                    [styles.privacyIconFriendsActive]: selectedPrivacy === privacyStatuses.friends,
                  })}
                />
                <div
                  onClick={() => this.handlePrivacyUpdate(privacyStatuses.public)}
                  className={classnames({
                    [styles.privacyIcon]: true,
                    [styles.privacyIconPublic]: true,
                    [styles.privacyIconPublicActive]: selectedPrivacy === privacyStatuses.public,
                  })}
                />
              </div>
              <div className={styles.postButton}>
                {createActivityStoryLoading ?
                  <Loader className={styles.storyCreateLoader} type='spin' height={33} width={33} />
                :
                  <Button
                    className={styles.postButton}
                    onClick={this.handleCreateActivityStory}
                    el='button'
                    type='action'
                    size='small'>
                    <FormattedMessage id='activitystory.post' defaultMessage='Post' />
                  </Button>
                }
              </div>
            </div>
          </Col>
        </Row>
      </div>
    )
  }
}

const intlComponent = injectIntl(CreateActivityStory)
export default connect(mapStateToProps, mapDispatchToActions)(intlComponent)



// WEBPACK FOOTER //
// ./src/components/home/feed/CreateActivityStory.jsx